import { Inject, Injectable } from '@angular/core';
import { UserModel } from '../shared/customObjects/userModel';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { PGPEncryptionService } from './pgpencryption.service';
import { ForensicUserProfileService } from './forensicUserProfile.service';
import { v4 as newGUID } from 'uuid';
export type PasswordType = 'Password' | 'PIN';

// =============================================================================
// Outcome of reloadFromServer(). The reload is a best-effort refresh of an
// already-successful write, so a failure is an expected state the caller has to
// decide about, not an exception: the value the user chose IS saved, only this
// browser's copy of the profile is behind. Modelled as a result rather than a
// thrown error so no caller can swallow it by omission.
// =============================================================================
export type ProfileReloadResult =
  | { reloaded: true }
  | { reloaded: false; error: unknown };

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  // -----------------------------------------------------------------------------
  // UserProfileInitGuardGuard requires the following to ensure profile loaded
  // -----------------------------------------------------------------------------
  private _isInitialized = new BehaviorSubject<boolean>(false);
  public isInitialized = this._isInitialized.asObservable();

  public user: UserModel = new UserModel();

  public showNewAccountSetup: boolean = false;

  constructor(
    @Inject(LOCAL_STORAGE) private storage: Storage,
    private pgpEncryptionService: PGPEncryptionService,
    private forensicUserProfileService: ForensicUserProfileService,
  ) {
    console.log('UserProfileService --> Constructor()');
  }

  // ===========================================================================
  // Re-read the profile from the API and replace the in-memory copy.
  //
  // WHY THIS EXISTS
  // `user` was populated exactly once per page load, by AuthService.initialize()
  // at startup. Every later write to the profile went out as a PATCH and was
  // never read back, so this object drifted from the server for the rest of the
  // browser session. The time zone made that visible: the account-setup dialog
  // saved a zone, the server stored it and rendered every note and notebook date
  // with it, and this copy still said `timeZone: ''` / `ianaTimeZoneId: null`.
  // The Settings dropdown and the "Your time zone is not set" banner both read
  // THIS object, so the app told a correctly-configured user they had no time
  // zone until they reloaded the page.
  //
  // The server is the authority on what was saved. A 200 from the PATCH says the
  // write was accepted; it does not say what the stored profile now looks like,
  // and it cannot say what the API derived from it (IanaTimeZoneId is computed
  // server-side from the Windows id, so the client has no way to synthesise it).
  // Read it back instead of assuming.
  //
  // Deliberately separate from AuthService.getAndSetUserProfile(), which does
  // the same fetch during startup and additionally sets the client encryption
  // KEK and routes 401/403/404 to a forced login. Those side effects belong to
  // establishing a session, not to refreshing one: a transient network blip
  // while saving a time zone must never bounce the user out of the app.
  // ===========================================================================
  reloadFromServer(): Observable<ProfileReloadResult> {
    return this.forensicUserProfileService.getUserProfile(false, this.user.userID).pipe(
      map((response) => {
        this.setUserProfile(response.result);
        return { reloaded: true } as ProfileReloadResult;
      }),
      catchError((error) => of({ reloaded: false, error } as ProfileReloadResult)),
    );
  }

  setUserProfile(user: any) {
    this.user.LoadFromApiResult(user);
    // --------------------------------------------------------------------------
    // Profile Loaded - Update Behavior Subject so that
    // individual pages that are protected by the UserProfileInitGuardGuard
    // can load
    // --------------------------------------------------------------------------
    this._isInitialized.next(true);
  }

  // ===========================================================================
  // Profile cache envelope. v2 (2026-08-09, .planning/UNLOCK-KEY-MIGRATION.md)
  // adds the PBKDF2 parameters so the unlock key can be derived properly from
  // the user's password; v1 blobs (no `v` field) were keyed on an unsalted
  // SHA-256 and are silently upgraded on their next successful unlock.
  // ===========================================================================
  async cacheUserProfile(KEK: string, type: PasswordType, kdfSalt?: string, kdfIterations?: number): Promise<string> {
    const contentEncryptionKey: string = newGUID();
    const encryptedCEK: string = await this.pgpEncryptionService.encryptData(contentEncryptionKey, KEK);
    const encryptedDataJSON: string = await this.pgpEncryptionService.encryptData(JSON.stringify(this.user), contentEncryptionKey);
    const envelope: any = { encryptedCEK, encryptedDataJSON, type };
    if (kdfSalt) {
      envelope.v = 2;
      envelope.kdfSalt = kdfSalt;
      envelope.kdfIterations = kdfIterations;
    }
    this.storage.setItem('userCached', JSON.stringify(envelope));
    return this.user.clientEncryptionKEK;
  }

  async loadUserProfileFromCache(KEK: string) {
    try {
      const userCached = this.storage.getItem('userCached');
      const { encryptedCEK, encryptedDataJSON } = JSON.parse(userCached);

      const decryptedCEK: string = await this.pgpEncryptionService.decryptData(encryptedCEK, KEK);
      const decryptedJSON: string = await this.pgpEncryptionService.decryptData(encryptedDataJSON, decryptedCEK);
      const user = JSON.parse(decryptedJSON);
      return user;
    } catch (error) {
      return null;
    }
  }

  // KDF parameters (and version) of the cached envelope, without decrypting it.
  getCachedEnvelopeMeta(): { v: number; kdfSalt?: string; kdfIterations?: number; type: PasswordType } | null {
    try {
      const userCached = this.storage.getItem('userCached');
      if (!userCached) {
        return null;
      }
      const { v, kdfSalt, kdfIterations, type } = JSON.parse(userCached);
      return { v: v || 1, kdfSalt, kdfIterations, type };
    } catch (error) {
      return null;
    }
  }

  getPasswordType(): PasswordType | null {
    const userCached = this.storage.getItem('userCached');
    if(!userCached) {
      return null;
    }
    const { type } = JSON.parse(userCached);
    return type;
  }

  isCachedUser() {
    return this.storage.getItem('userCached');
  }
}
